import * as React from "react";
import { PageContainer, Card } from "./Card";
import { PageHeader, SectionHeader } from "./PageHeader";
import { Table, Thead, Tbody, Tr, Th, Td, TdActions } from "./Table";
import { Button, IconButton } from "./Button";
import { Modal, ModalBody } from "./Modal";
import { ConfirmModal } from "./ConfirmModal";
import { Field, Input, Select, Textarea } from "./Form";
import { Banner, ListStates } from "./States";
import { Toolbar, SearchInput } from "./Toolbar";
import { IconInput } from "./Icon";
import { useAsync, useAction } from "../general/useAsync";
import { useToast } from "../general/Toast";

/**
 * A declarative list + create + edit + delete screen.
 *
 * The Structure and Management slices are eleven entities behind the same five-verb controller shape
 * (List / Get / Create / Update / Delete), differing only in their fields. Hand-writing eleven
 * near-identical screens is how the reference app ended up with the same table markup copied a dozen
 * times and slowly drifting. Instead each entity declares a `CrudResource` and this renders it.
 *
 * Anything that is genuinely not CRUD - the assessment structure tree, mark entry, the procedure
 * editor - does NOT go through here. This is for flat vocabularies and lookup tables.
 */

export interface CrudColumn<T> {
    header: string;
    render: (item: T) => React.ReactNode;
    align?: "left" | "right" | "center";
    className?: string;
}

export interface CrudFieldOption {
    value: string;
    label: string;
}

export interface CrudField<D> {
    name: keyof D & string;
    label: string;
    type?: "text" | "textarea" | "number" | "date" | "select";
    required?: boolean;
    placeholder?: string;
    hint?: React.ReactNode;
    /** For `type: "select"`. Build these in the caller so they can close over loaded lookups. */
    options?: CrudFieldOption[];
}

export interface CrudResource<T, D extends Record<string, any>> {
    /** Page chrome. */
    title: string;
    description?: React.ReactNode;
    icon?: IconInput;
    /** Used in button labels, modal titles and confirmations: "Faculty" / "Faculties". */
    singular: string;
    plural: string;

    list: () => Promise<T[]>;
    create: (draft: D) => Promise<unknown>;
    update: (id: string, draft: D) => Promise<unknown>;
    remove: (id: string) => Promise<unknown>;

    idOf: (item: T) => string;
    /** How the item is named in a confirmation prompt. */
    labelOf: (item: T) => string;
    /** Free-text haystack for the search box. Defaults to `labelOf`. */
    searchOf?: (item: T) => string;

    columns: CrudColumn<T>[];
    fields: CrudField<D>[];
    /** Build a blank draft, or one populated from an existing item. */
    toDraft: (item?: T) => D;

    /** When false the screen is read-only: no create, edit or delete affordances are rendered. */
    canWrite: boolean;
    /** Deletes are hidden when false even if `canWrite` is true - some vocabularies archive instead. */
    canDelete?: boolean;
}

export interface CrudPageProps<T, D extends Record<string, any>> {
    resource: CrudResource<T, D>;
    /** Rendered between the header and the table - filters, warnings, related actions. */
    children?: React.ReactNode;
    /** Re-run the list whenever one of these changes (e.g. a selected parent id). */
    deps?: React.DependencyList;
    /**
     * "page" (default) owns the whole screen: page container and page header.
     * "section" drops both, for embedding several of these under one page's tabs.
     */
    variant?: "page" | "section";
}

export function CrudPage<T, D extends Record<string, any>>({ resource, children, deps = [], variant = "page" }: CrudPageProps<T, D>) {
    const toast = useToast();
    const [query, setQuery] = React.useState("");
    const [editing, setEditing] = React.useState<{ mode: "create" | "edit"; item?: T } | null>(null);
    const [deleting, setDeleting] = React.useState<T | null>(null);

    const items = useAsync(() => resource.list(), deps, { initial: [] as T[] });
    const rows = (items.data ?? []) as T[];

    const search = resource.searchOf ?? resource.labelOf;
    const filtered = React.useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return rows;
        return rows.filter((item) => (search(item) ?? "").toLowerCase().includes(needle));
    }, [rows, query, search]);

    const removal = useAction(async () => {
        if (!deleting) return;
        await resource.remove(resource.idOf(deleting));
        toast.success(`${resource.singular} deleted.`);
        setDeleting(null);
        items.refresh();
    });

    const canDelete = resource.canWrite && resource.canDelete !== false;

    const addButton = resource.canWrite && (
        <Button icon={["fad", "plus"]} onClick={() => setEditing({ mode: "create" })}>
            Add {resource.singular.toLowerCase()}
        </Button>
    );

    const Wrapper = variant === "page" ? PageContainer : React.Fragment;

    return (
        <Wrapper>
            {variant === "page" ? (
                <PageHeader title={resource.title} description={resource.description} icon={resource.icon} actions={addButton} />
            ) : (
                <SectionHeader title={resource.title} description={resource.description} actions={addButton} />
            )}

            {children}

            <Toolbar>
                <SearchInput value={query} onChange={setQuery} placeholder={`Search ${resource.plural.toLowerCase()}...`} />
                <span className="text-xs text-text-weak">
                    {filtered.length} of {rows.length}
                </span>
            </Toolbar>

            <Card>
                <ListStates
                    isLoading={items.isLoading}
                    error={items.error}
                    onRetry={items.refresh}
                    isEmpty={filtered.length === 0}
                    empty={{
                        icon: resource.icon,
                        title: query ? `No ${resource.plural.toLowerCase()} match your search` : `No ${resource.plural.toLowerCase()} yet`,
                        hint: query
                            ? "Try a different search term."
                            : resource.canWrite
                            ? `Get started by adding a ${resource.singular.toLowerCase()}.`
                            : undefined,
                    }}
                >
                    <Table>
                        <Thead>
                            <Tr>
                                {resource.columns.map((c) => (
                                    <Th key={c.header} align={c.align}>
                                        {c.header}
                                    </Th>
                                ))}
                                {resource.canWrite && <Th align="right">Actions</Th>}
                            </Tr>
                        </Thead>
                        <Tbody>
                            {filtered.map((item) => (
                                <Tr key={resource.idOf(item)}>
                                    {resource.columns.map((c) => (
                                        <Td key={c.header} align={c.align} className={c.className}>
                                            {c.render(item)}
                                        </Td>
                                    ))}
                                    {resource.canWrite && (
                                        <TdActions>
                                            <IconButton
                                                icon={["fad", "pen-to-square"]}
                                                label={`Edit ${resource.labelOf(item)}`}
                                                onClick={() => setEditing({ mode: "edit", item })}
                                            />
                                            {canDelete && (
                                                <IconButton
                                                    icon={["fad", "trash"]}
                                                    variant="outline-danger"
                                                    label={`Delete ${resource.labelOf(item)}`}
                                                    onClick={() => setDeleting(item)}
                                                />
                                            )}
                                        </TdActions>
                                    )}
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </ListStates>
            </Card>

            <CrudFormModal
                resource={resource}
                state={editing}
                onClose={() => setEditing(null)}
                onSaved={() => {
                    setEditing(null);
                    items.refresh();
                }}
            />

            <ConfirmModal
                isOpen={deleting !== null}
                onClose={() => setDeleting(null)}
                onConfirm={removal.run}
                title={`Delete ${resource.singular.toLowerCase()}`}
                icon={["fad", "trash"]}
                busy={removal.busy}
                error={removal.error}
                confirmLabel="Delete"
                body={
                    <>
                        Delete <strong>{deleting ? resource.labelOf(deleting) : ""}</strong>? This cannot be undone. Records that
                        still reference it will block the delete until those are removed.
                    </>
                }
            />
        </Wrapper>
    );
}

/**
 * One modal serving both create and edit, keyed off `mode` - ScadsysIdentity's UserFormModal pattern,
 * which halves the modal count. Draft state resets whenever the modal opens so a half-typed create
 * never bleeds into the next edit.
 */
function CrudFormModal<T, D extends Record<string, any>>({
    resource,
    state,
    onClose,
    onSaved,
}: {
    resource: CrudResource<T, D>;
    state: { mode: "create" | "edit"; item?: T } | null;
    onClose: () => void;
    onSaved: () => void;
}) {
    const toast = useToast();
    const isOpen = state !== null;
    const mode = state?.mode ?? "create";
    const item = state?.item;

    const [draft, setDraft] = React.useState<D>(() => resource.toDraft());

    React.useEffect(() => {
        if (isOpen) setDraft(resource.toDraft(item));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, mode, item && resource.idOf(item)]);

    const save = useAction(async () => {
        if (mode === "create") {
            await resource.create(draft);
            toast.success(`${resource.singular} created.`);
        } else {
            await resource.update(resource.idOf(item as T), draft);
            toast.success(`${resource.singular} updated.`);
        }
        onSaved();
    });

    const set = (name: string, value: any) => setDraft((d) => ({ ...d, [name]: value }));

    const missingRequired = resource.fields.some((f) => f.required && !String(draft?.[f.name] ?? "").trim());

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`${mode === "create" ? "Add" : "Edit"} ${resource.singular.toLowerCase()}`}
            icon={resource.icon}
            footer={
                <>
                    <Button variant="outline-secondary" onClick={onClose} disabled={save.busy}>
                        Cancel
                    </Button>
                    <Button onClick={save.run} loading={save.busy} disabled={missingRequired}>
                        {mode === "create" ? "Create" : "Save changes"}
                    </Button>
                </>
            }
        >
            <ModalBody>
                {resource.fields.map((field) => {
                    const value = (draft?.[field.name] ?? "") as any;

                    return (
                        <Field key={field.name} label={field.label} required={field.required} hint={field.hint}>
                            {field.type === "textarea" ? (
                                <Textarea value={value} placeholder={field.placeholder} onChange={(e) => set(field.name, e.target.value)} />
                            ) : field.type === "select" ? (
                                // No `?? ""` here: Select renders the placeholder for any value that is not
                                // undefined, so an empty string added a blank, unlabelled first option to every
                                // field that did not ask for one - which a closed-set field like Status or Role
                                // could then be submitted with.
                                <Select value={value} placeholder={field.placeholder} onChange={(e) => set(field.name, e.target.value)}>
                                    {(field.options ?? []).map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </Select>
                            ) : (
                                <Input
                                    type={field.type ?? "text"}
                                    value={value}
                                    placeholder={field.placeholder}
                                    onChange={(e) => set(field.name, e.target.value)}
                                />
                            )}
                        </Field>
                    );
                })}

                {save.error && <Banner tone="danger">{save.error}</Banner>}
            </ModalBody>
        </Modal>
    );
}

export default CrudPage;
